import { useState, useEffect, useCallback } from 'react'
import api from '../../lib/axios'
import { PageHeader, Card, Spinner, EmptyState, StatusBadge } from '../../components/ui'
import ShareModal          from '../../components/sharing/ShareModal'
import VersionHistoryPanel from '../../components/sharing/VersionHistoryPanel'
import DiffStatusBanner    from '../../components/diff/DiffStatusBanner'
import toast from 'react-hot-toast'

function fmt(iso) {
  return iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''
}

export default function Documents() {
  const [docs, setDocs]           = useState([])
  const [filter, setFilter]       = useState('all')
  const [loading, setLoading]     = useState(true)
  const [shareDoc, setShareDoc]   = useState(null)   // doc being shared
  const [historyDoc, setHistoryDoc] = useState(null) // doc showing version history
  const [expanded, setExpanded]   = useState(null)   // row expanded for diff banner

  const load = useCallback(() => {
    setLoading(true)
    api.get('/documents').then(r => {
      const data = r.data?.data ?? r.data
      setDocs(Array.isArray(data) ? data : [])
    }).finally(() => setLoading(false))
  }, [])

  useEffect(() => { load() }, [load])

  const filtered = filter === 'all' ? docs : docs.filter(d => d.status === filter)

  const generateNonTechnical = async (doc) => {
    const tid = toast.loading('Generating non-technical PDF…')
    try {
      const res = await api.post(`/repositories/${doc.repository_id}/generate-pdf/non-technical`)
      toast.success('PDF ready!', { id: tid })
      window.open(res.data.download_url, '_blank')
    } catch { toast.error('Generation failed.', { id: tid }) }
  }

  return (
    <div className="pb-10 animate-fade-in" style={{ background: 'linear-gradient(180deg,#0d0521 0%,#07020f 100%)', minHeight: '100%' }}>
      <PageHeader title="Documents" subtitle={`${docs.length} generated`} />
      <div className="px-8">

        {/* Filter tabs */}
        {docs.length > 0 && (
          <div className="flex gap-1 mb-5 p-1 rounded-xl bg-white/4 border border-white/6 w-fit">
            {['all','complete','generating','failed'].map(f => (
              <button key={f} onClick={() => setFilter(f)}
                className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  filter === f ? 'bg-cosmic-800 text-white border border-white/8' : 'text-white/40 hover:text-white'}`}>
                {f.charAt(0).toUpperCase()+f.slice(1)}
                <span className={`ml-1.5 font-mono text-[10px] ${filter === f ? 'text-purple-400' : 'text-white/25'}`}>
                  {f === 'all' ? docs.length : docs.filter(d => d.status === f).length}
                </span>
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-48"><Spinner size="lg" /></div>
        ) : filtered.length === 0 ? (
          <Card><EmptyState icon="📄" title="No documents yet"
            description="Documents are generated from your repositories via the automation panel." /></Card>
        ) : (
          <div className="space-y-3">
            {filtered.map(doc => (
              <div key={doc.id} className="glass-card overflow-hidden">

                {/* Diff banner — shown when doc has a base SHA to compare */}
                {doc.status === 'complete' && doc.last_generated_commit_sha && (
                  <DiffStatusBanner type="document" id={doc.id} onRegenQueued={load} />
                )}

                <div className="flex items-center gap-4 px-5 py-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-semibold text-white truncate">{doc.title}</p>
                      {doc.generation_mode === 'diff' && (
                        <span className="text-[9px] font-bold uppercase tracking-wide text-blue-400 bg-blue-500/10 px-1.5 py-0.5 rounded-full border border-blue-500/20">DIFF</span>
                      )}
                      {doc.is_public && (
                        <span className="text-[9px] font-bold uppercase tracking-wide text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded-full border border-emerald-500/20">SHARED</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <StatusBadge status={doc.status} />
                      <span className="text-xs text-white/35">{doc.repo_name}</span>
                      <span className="text-xs text-white/25">v{doc.version ?? 1}</span>
                      {doc.view_count > 0 && <span className="text-xs text-white/25">👁 {doc.view_count}</span>}
                      <span className="text-xs text-white/25">{fmt(doc.created_at)}</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {doc.status === 'complete' && (
                      <>
                        <button onClick={() => generateNonTechnical(doc)}
                          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-blue-500/10 border border-blue-500/20 text-blue-300 text-xs font-medium hover:bg-blue-500/20 transition-all">
                          📄 Non-Tech PDF
                        </button>
                        <button onClick={() => setHistoryDoc(historyDoc?.id === doc.id ? null : doc)}
                          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/10 text-white/50 text-xs font-medium hover:bg-white/10 transition-all">
                          🕐 History
                        </button>
                        <button onClick={() => setShareDoc(doc)}
                          className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-all ${
                            doc.is_public
                              ? 'bg-emerald-500/10 border-emerald-500/25 text-emerald-300 hover:bg-emerald-500/20'
                              : 'bg-white/5 border-white/10 text-white/50 hover:bg-white/10'}`}>
                          🔗 Share
                        </button>
                        {doc.public_url && (
                          <a href={doc.public_url} target="_blank" rel="noreferrer"
                            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-purple-500/10 border border-purple-500/20 text-purple-300 text-xs font-medium hover:bg-purple-500/20 transition-all">
                            ↓ Download
                          </a>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Version history panel */}
                {historyDoc?.id === doc.id && (
                  <div className="border-t border-white/6 px-5 py-4">
                    <VersionHistoryPanel type="document" id={doc.id} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Share modal */}
      {shareDoc && (
        <ShareModal type="document" id={shareDoc.id} title={shareDoc.title}
          onClose={() => { setShareDoc(null); load() }} />
      )}
    </div>
  )
}
